import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { toast } from 'sonner';

import { AccountFormFields } from '@/accounts/account-form';
import { AccountImportDialog } from '@/accounts/account-import-dialog';
import {
  useAccounts,
  useCreateAccount,
  type AccountRecord,
  type AccountSort,
  type AccountsListFilter,
} from '@/accounts/queries';
import { formatCooldownCountdown } from '@/accounts/relative-time';
import {
  ACCOUNT_ROLES,
  ACCOUNT_STATUSES,
  AccountCreateSchema,
  type AccountCreateInput,
  type AccountRole,
  type AccountStatus,
} from '@/accounts/schemas';
import { humanErrorMessage } from '@/api/errors';
import { DataTable } from '@/components/data-table';
import { FormDialog } from '@/components/form-dialog';
import { PageShell } from '@/components/page-shell';
import { StatusDot } from '@/components/status-dot';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useListQueryRoute } from '@/lib/use-list-query-route';

const ALL = '__all__';

export const AccountsListPage = () => {
  // Phase 8.5-05 — URL-driven list state (ADR-014).
  const lq = useListQueryRoute<AccountsListFilter, AccountSort>({
    from: '/_protected/accounts',
    defaultFilter: {},
    defaultSort: { field: 'createdAt', dir: 'desc' },
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const accountsQuery = useAccounts({
    filter: lq.filter,
    sort: lq.sort,
    page: lq.page,
    pageSize: lq.pageSize,
  });
  const items = accountsQuery.data?.items ?? [];
  const total = accountsQuery.data?.total ?? 0;

  const createAccount = useCreateAccount();

  const columns: ColumnDef<AccountRecord>[] = [
    {
      id: 'steamLogin',
      header: 'Login',
      meta: { sortField: 'steamLogin' },
      cell: ({ row }) => (
        <Link
          to="/accounts/$accountId"
          params={{ accountId: row.original.id }}
          className="font-medium hover:underline"
        >
          {row.original.steamLogin}
        </Link>
      ),
    },
    {
      id: 'steamId64',
      header: 'Steam ID',
      cell: ({ row }) => <span className="font-mono text-xs">{row.original.steamId64}</span>,
    },
    {
      id: 'role',
      header: 'Роль',
      cell: ({ row }) => (
        <Badge variant={row.original.role === 'storage' ? 'secondary' : 'default'}>
          {row.original.role === 'storage' ? 'Склад' : 'Бот'}
        </Badge>
      ),
    },
    {
      id: 'status',
      header: 'Статус',
      meta: { sortField: 'status' },
      cell: ({ row }) => <StatusDot domain="account" state={row.original.status} />,
    },
    {
      id: 'activeLimit',
      header: 'Активные',
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {row.original.activeTrades}/{row.original.maxActiveTrades}
        </span>
      ),
    },
    {
      id: 'email',
      header: 'Email',
      cell: ({ row }) => row.original.email ?? '—',
    },
    {
      id: 'cooldown',
      header: 'Cooldown',
      cell: ({ row }) => {
        const r = row.original;
        if (r.status !== 'cooldown') return <span className="text-muted-foreground">—</span>;
        return (
          <span
            className="text-sm text-muted-foreground"
            title={new Date(r.availableAfter).toLocaleString('ru-RU')}
          >
            {formatCooldownCountdown(r.availableAfter)}
          </span>
        );
      },
    },
    {
      id: 'proxy',
      header: 'Proxy',
      cell: ({ row }) => {
        const r = row.original;
        if (r.proxyUrl === null) return <span className="text-muted-foreground">—</span>;
        return (
          <span className="inline-flex items-center gap-2">
            <StatusDot domain="proxy" state={r.proxyHealth ?? 'down'} showLabel={false} />
            <span className="truncate font-mono text-xs" title={r.proxyUrl}>
              {r.proxyUrl}
            </span>
          </span>
        );
      },
    },
    {
      id: 'createdAt',
      header: 'Создан',
      meta: { sortField: 'createdAt' },
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {new Date(row.original.createdAt).toLocaleString('ru-RU')}
        </span>
      ),
    },
  ];

  const handleSubmit = (values: AccountCreateInput): void => {
    createAccount.mutate(values, {
      onSuccess: () => {
        toast.success('Аккаунт создан');
        setCreateOpen(false);
      },
      onError: (err) => toast.error(humanErrorMessage(err)),
    });
  };

  const filterSlot = (
    <>
      <label className="flex items-center gap-2 text-sm text-muted-foreground">
        <input
          type="checkbox"
          checked={lq.filter.includeDeleted ?? false}
          onChange={(e) => {
            lq.updateQuery({
              filter: { includeDeleted: e.target.checked ? true : undefined },
            });
          }}
          className="h-4 w-4"
        />
        Показывать удалённых
      </label>
      <div className="w-40">
        <Select
          value={lq.filter.status ?? ALL}
          onValueChange={(v) => {
            lq.updateQuery({
              filter: { status: v === ALL ? undefined : (v as AccountStatus) },
            });
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Все статусы</SelectItem>
            {ACCOUNT_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="w-32">
        <Select
          value={lq.filter.role ?? ALL}
          onValueChange={(v) => {
            lq.updateQuery({
              filter: { role: v === ALL ? undefined : (v as AccountRole) },
            });
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Все роли</SelectItem>
            {ACCOUNT_ROLES.map((r) => (
              <SelectItem key={r} value={r}>
                {r === 'storage' ? 'Склад' : 'Бот'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <span className="text-sm text-muted-foreground">Всего: {total}</span>
    </>
  );

  return (
    <div className="space-y-4 p-6">
      <PageShell
        title="Аккаунты"
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setImportOpen(true);
              }}
            >
              Импорт TradeOn 7z
            </Button>
            <Button
              onClick={() => {
                setCreateOpen(true);
              }}
            >
              Создать аккаунт
            </Button>
          </>
        }
      >
        {accountsQuery.isLoading ? (
          <div className="p-8 text-center text-muted-foreground">Загрузка…</div>
        ) : (
          <DataTable
            columns={columns}
            data={items}
            emptyMessage="Нет аккаунтов"
            filterSlot={filterSlot}
            sort={{ field: lq.sort.field, dir: lq.sort.dir }}
            onSortChange={(next) => {
              lq.updateQuery({
                sort: { field: next.field as AccountSort | null, dir: next.dir },
              });
            }}
            pagination={{
              page: lq.page,
              pageSize: lq.pageSize,
              total,
              onPageChange: (page) => {
                lq.updateQuery({ page });
              },
            }}
          />
        )}
      </PageShell>

      <FormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="Новый Steam-аккаунт"
        schema={AccountCreateSchema}
        defaultValues={{
          steamLogin: '',
          steamId64: '',
          password: '',
          sharedSecret: '',
          identitySecret: '',
          revocationCode: '',
          email: '',
          emailPassword: '',
          refreshToken: '',
        }}
        onSubmit={handleSubmit}
        isPending={createAccount.isPending}
      >
        <AccountFormFields />
      </FormDialog>

      <AccountImportDialog open={importOpen} onOpenChange={setImportOpen} />
    </div>
  );
};
